import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  CircularProgress,
  Alert,
  useTheme,
  alpha,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import API from '../api/axios';
import TourCard from '../components/common/TourCard';
import CategoryTabs from '../components/common/CategoryTabs';
import useCategories from '../hooks/useCategories';
import ExclusiveOffers from '../components/ExclusiveOffers';
import ExploreThemes from '../components/ExploreThemes';
import NewsletterSubscribe from '../components/NewsletterSubscribe';

// ============ Styled Components ============

const HeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(4),
  flexWrap: 'wrap',
  gap: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
}));

const Title = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '2rem',
  color: theme.palette.text.primary,
  fontFamily: 'sans-serif',
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.4rem',
  },
}));

const GridContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
  gap: '24px',
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
    gap: '16px',
  },
}));

const LoadMoreWrapper = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  paddingTop: 32,
  paddingBottom: 16,
});

const PAGE_LIMIT = 12;

const Tours = () => {
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTab, setSelectedTab] = useState('Domestic');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  // Dynamic categories from backend
  const { categories: CATEGORIES } = useCategories(true); // includeAll = true adds 'All'

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const theme = useTheme();

  // Refs to keep current values for callbacks
  const currentCategoryRef = useRef(selectedTab);
  const currentSearchRef = useRef(searchQuery);

  // Keep refs in sync
  useEffect(() => {
    currentCategoryRef.current = selectedTab;
  }, [selectedTab]);

  useEffect(() => {
    currentSearchRef.current = searchQuery;
  }, [searchQuery]);

  const fetchTours = useCallback(async (category, search = '', pageNum = 1, append = false) => {
    try {
      if (pageNum === 1) {
        setLoading(true);
        setError(null);
      } else {
        setLoadingMore(true);
      }

      const params = new URLSearchParams();
      if (category && category !== 'All') params.append('category', category);
      if (search) params.append('search', search);
      params.append('page', String(pageNum));
      params.append('limit', String(PAGE_LIMIT));

      const url = `/tours${params.toString() ? `?${params.toString()}` : ''}`;
      const { data } = await API.get(url);

      // Handle both old array format and new paginated object format
      const newTours = Array.isArray(data) ? data : (data?.tours || []);
      if (append) {
        setTours(prev => [...prev, ...newTours]);
      } else {
        setTours(newTours);
      }
      setPage(data?.page || 1);
      setHasMore(data?.hasMore ?? false);
      setTotalCount(data?.total || newTours.length);
    } catch (err) {
      if (pageNum === 1) {
        setError('Failed to load tours. Please try again later.');
      }
      console.error('Error fetching tours:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  // Load more tours (next page)
  const handleLoadMore = useCallback(() => {
    if (loadingMore || !hasMore) return;
    const nextPage = page + 1;
    fetchTours(currentCategoryRef.current, currentSearchRef.current, nextPage, true);
  }, [page, hasMore, loadingMore, fetchTours]);

  // Page mode: sync with URL category & search params on mount / param change
  // NOTE: CATEGORIES is intentionally NOT in the dependency array to prevent
  // a double API call when categories load asynchronously after mount.
  // If categories haven't loaded yet, we accept the URL param as-is.
  useEffect(() => {
    const categoryParam = searchParams.get('category');
    const searchParam = searchParams.get('search') || '';

    let activeCat = 'Domestic';
    if (categoryParam) {
      // If CATEGORIES is still empty (not loaded yet), accept the param anyway
      if (CATEGORIES.length === 0 || CATEGORIES.includes(categoryParam)) {
        activeCat = categoryParam;
      }
    } else if (searchParam) {
      activeCat = 'All';
    }

    setSelectedTab(activeCat);
    setSearchQuery(searchParam);
    setPage(1);
    setHasMore(false);
    setTours([]);
    fetchTours(activeCat, searchParam, 1, false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, fetchTours]);

  const handleTabChange = (category) => {
    setSelectedTab(category);
    setSearchQuery('');
    navigate(`/tours?category=${category}`, { replace: true });
  };

  return (
    <Box sx={{ bgcolor: '#ffffff', minHeight: '100vh', py: { xs: 4, md: 8 } }}>
      <Container maxWidth="xl">

        {/* Search Results Heading */}
        {searchQuery && !loading && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Search results for "<Typography component="span" sx={{ color: 'primary.main', fontWeight: 700 }}>{searchQuery}</Typography>"
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {totalCount} tour{totalCount !== 1 ? 's' : ''} found
            </Typography>
          </Box>
        )}

        {/* Header Title & Tabs */}
        <HeaderContainer>
          <Title>
            {searchQuery ? 'Matching Tours' : 'Our Tours'}
          </Title>
          <CategoryTabs
            categories={CATEGORIES}
            selectedTab={selectedTab}
            onTabChange={handleTabChange}
          />
        </HeaderContainer>

        {/* Content */}
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="340px">
            <CircularProgress size={48} sx={{ color: theme.palette.primary.main }} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ my: 4, borderRadius: 2 }}>{error}</Alert>
        ) : tours.length === 0 ? (
          <Box textAlign="center" py={8}>
            <Typography variant="h6" color="text.secondary">
              {searchQuery ? (
                <>No tours found matching "<Typography component="span" sx={{ fontWeight: 700 }}>{searchQuery}</Typography>" in {selectedTab} category.</>
              ) : (
                <>No tours found for "{selectedTab}".</>
              )}
            </Typography>
          </Box>
        ) : (
          <>
            <GridContainer>
              {tours.map((tour) => (
                <TourCard
                  key={tour._id}
                  tour={tour}
                  variant="grid"
                  showDuration
                />
              ))}
            </GridContainer>

            {/* Load More Button */}
            {hasMore && (
              <LoadMoreWrapper>
                <Button
                  variant="outlined"
                  size="large"
                  disabled={loadingMore}
                  onClick={handleLoadMore}
                  sx={{
                    borderRadius: '50px',
                    px: 6,
                    py: 1.2,
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: '1rem',
                    borderColor: alpha(theme.palette.primary.main, 0.3),
                    color: 'primary.main',
                    '&:hover': {
                      borderColor: 'primary.main',
                      bgcolor: alpha(theme.palette.primary.main, 0.04),
                    },
                  }}
                >
                  {loadingMore ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1.5, color: theme.palette.primary.main }} />
                      Loading...
                    </>
                  ) : (
                    <>Load More Tours ({tours.length} of {totalCount})</>
                  )}
                </Button>
              </LoadMoreWrapper>
            )}

            {/* End of results message */}
            {!hasMore && tours.length > 0 && (
              <Box textAlign="center" py={4}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  All {totalCount} tour{totalCount !== 1 ? 's' : ''} loaded.
                </Typography>
              </Box>
            )}
          </>
        )}

      </Container>
      <ExploreThemes/>
      <ExclusiveOffers/>
      <NewsletterSubscribe/>
    </Box>
  );
};

export default Tours;